import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Trophy, Flame, CalendarCheck, Target, ListChecks, Crown, Medal, Award, Star } from "lucide-react";
import { Avatar } from "@/components/Avatar";

const ROLE_LABEL = { user: "User", manager: "Manager", super_manager: "Super Manager", super_admin: "Super Admin" };
const MEDAL = { 1: "bg-amber-400 text-amber-950", 2: "bg-slate-300 text-slate-800", 3: "bg-orange-400 text-orange-950" };

const HOF_TITLE = {
  1: { label: "Pulse Champion", icon: Crown, cls: "from-amber-400/25 to-amber-500/5 border-amber-400/40", badge: "bg-amber-400 text-amber-950" },
  2: { label: "Pulse Excellence", icon: Medal, cls: "from-slate-300/25 to-slate-400/5 border-slate-300/40", badge: "bg-slate-300 text-slate-800" },
  3: { label: "Pulse Achiever", icon: Award, cls: "from-orange-400/25 to-orange-500/5 border-orange-400/40", badge: "bg-orange-400 text-orange-950" },
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function Leaderboard() {
  const now = new Date();
  const [mode, setMode] = useState("month"); // month | rolling
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = mode === "month" ? { month: `${year}-${String(month).padStart(2, "0")}` } : { days };
      const { data } = await api.get("/reports/leaderboard", { params });
      setRows(data.rows || []);
    } catch (e) {
      toast.error("Could not load leaderboard");
    } finally { setLoading(false); }
  }, [mode, month, year, days]);
  useEffect(() => { load(); }, [load]);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(y);
  const isCurrentMonth = month === now.getMonth() + 1 && year === now.getFullYear();

  return (
    <div data-testid="leaderboard">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Trophy className="h-5 w-5" /></div>
        <div className="mr-auto">
          <h2 className="font-heading text-xl font-bold">Leaderboard</h2>
          <p className="text-sm text-muted-foreground">{mode === "month" ? "Monthly — resets on the 1st of each month. Rewards consistency & follow-through." : "Rolling window. Rewards consistency & follow-through."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5">
            <Button size="sm" variant={mode === "month" ? "default" : "secondary"} className="rounded-full"
              onClick={() => { setMode("month"); setMonth(now.getMonth() + 1); setYear(now.getFullYear()); }} data-testid="lb-mode-month">Monthly</Button>
            {[7, 30, 90].map((d) => (
              <Button key={d} size="sm" variant={mode === "rolling" && days === d ? "default" : "secondary"} className="rounded-full"
                onClick={() => { setMode("rolling"); setDays(d); }} data-testid={`lb-days-${d}`}>{d}d</Button>
            ))}
          </div>
          {mode === "month" && (
            <div className="flex items-center gap-2">
              {!isCurrentMonth && <Button size="sm" variant="ghost" className="rounded-full text-xs h-7" onClick={() => { setMonth(now.getMonth() + 1); setYear(now.getFullYear()); }} data-testid="lb-current-month">This month</Button>}
              <select className="h-9 rounded-lg border border-border bg-background px-2 text-sm" value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="lb-month-select">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <select className="h-9 rounded-lg border border-border bg-background px-2 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="lb-year-select">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>
      {mode === "month" && (
        <p className="text-xs text-muted-foreground mb-4" data-testid="lb-month-label">
          Showing <strong>{MONTHS[month - 1]} {year}</strong>{isCurrentMonth ? " (current month — in progress)" : ""}
        </p>
      )}

      <Card className="p-4 sm:p-5 rounded-xl mb-4 bg-secondary/40">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong>How scoring works:</strong> points for each day you report (+streak bonus), completed plan items
          weighted by your completion rate (adding items you never finish <em>lowers</em> your score), tasks capped at
          5/day, and a quality bonus only when a day has a real summary <em>and</em> actual tasks. <strong>Off</strong> days
          are neutral — they don't score and keep your streak intact. <strong>Leave</strong> days earn no points and break
          the streak. Filler can't win.
        </p>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center rounded-xl" data-testid="lb-empty"><p className="text-muted-foreground">No activity yet.</p></Card>
      ) : (
        <div className="space-y-2" data-testid="lb-list">
          {rows.map((r) => (
            <Card key={r.user_id} className={`p-4 rounded-xl flex items-center gap-3 ${r.rank <= 3 ? "border-primary/30" : ""}`} data-testid={`lb-row-${r.user_id}`}>
              <div className={`h-9 w-9 rounded-full flex items-center justify-center font-heading font-bold text-sm shrink-0 ${MEDAL[r.rank] || "bg-secondary text-muted-foreground"}`}>{r.rank}</div>
              <Avatar src={r.avatar} name={r.name} className="h-9 w-9 text-sm" testid={`lb-avatar-${r.user_id}`} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold leading-tight truncate">{r.name} <span className="text-[11px] font-normal text-muted-foreground">· {ROLE_LABEL[r.role]}</span></p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1"><CalendarCheck className="h-3 w-3" /> {r.days_reported} days</span>
                  <span className="flex items-center gap-1"><Flame className="h-3 w-3" /> {r.streak} streak</span>
                  <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" /> {r.plan_done} done · {r.completion_rate}%</span>
                  <span className="flex items-center gap-1"><Target className="h-3 w-3" /> {r.tasks} task pts</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading text-2xl font-bold text-primary leading-none" data-testid={`lb-score-${r.user_id}`}>{r.score}</p>
                <p className="text-[11px] text-muted-foreground">points</p>
              </div>
            </Card>
          ))}
        </div>
      )}
      <HallOfFame />
    </div>
  );
}

function HallOfFame() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const mm = `${year}-${String(month).padStart(2, "0")}`;
      const { data } = await api.get("/reports/leaderboard", { params: { month: mm } });
      setRows((data.rows || []).slice(0, 10));
    } catch (e) { toast.error("Could not load Hall of Fame"); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(y);
  const top3 = rows.filter((r) => r.rank <= 3);
  const rest = rows.filter((r) => r.rank >= 4);

  return (
    <div className="mt-10" data-testid="hall-of-fame">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-amber-400/15 text-amber-500 flex items-center justify-center"><Crown className="h-5 w-5" /></div>
        <div className="mr-auto">
          <h2 className="font-heading text-xl font-bold">Hall of Fame</h2>
          <p className="text-sm text-muted-foreground">Monthly top performers — resets on the 1st of every month.</p>
        </div>
        <div className="flex gap-2">
          <select className="h-9 rounded-lg border border-border bg-background px-2 text-sm" value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="hof-month-select">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="h-9 rounded-lg border border-border bg-background px-2 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="hof-year-select">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center rounded-xl" data-testid="hof-empty"><p className="text-muted-foreground">No activity in {MONTHS[month - 1]} {year}.</p></Card>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            {top3.map((r) => {
              const t = HOF_TITLE[r.rank];
              const Icon = t.icon;
              return (
                <Card key={r.user_id} className={`p-5 rounded-xl bg-gradient-to-b ${t.cls} border`} data-testid={`hof-top-${r.rank}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`h-7 w-7 rounded-full flex items-center justify-center ${t.badge}`}><Icon className="h-4 w-4" /></span>
                    <span className="font-heading font-bold text-sm">{t.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Avatar src={r.avatar} name={r.name} className="h-12 w-12 text-base" testid={`hof-avatar-${r.user_id}`} />
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{r.name}</p>
                      <p className="text-[11px] text-muted-foreground">{ROLE_LABEL[r.role]} · {r.score} pts</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          {rest.length > 0 && (
            <Card className="p-4 sm:p-5 rounded-xl" data-testid="hof-contributors">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> Top Contributors</p>
              <div className="space-y-2">
                {rest.map((r) => (
                  <div key={r.user_id} className="flex items-center gap-3" data-testid={`hof-row-${r.user_id}`}>
                    <span className="h-7 w-7 rounded-full bg-secondary text-muted-foreground flex items-center justify-center font-heading font-bold text-xs shrink-0">{r.rank}</span>
                    <Avatar src={r.avatar} name={r.name} className="h-8 w-8 text-sm" testid={`hof-avatar-${r.user_id}`} />
                    <p className="flex-1 min-w-0 font-medium truncate text-sm">{r.name} <span className="text-[11px] font-normal text-muted-foreground">· {ROLE_LABEL[r.role]}</span></p>
                    <span className="text-sm font-bold text-primary shrink-0">{r.score} pts</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
